// src/pages/CustomerReview.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaStar, FaCloudUploadAlt, FaTimes, FaCheckCircle } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';

export default function CustomerReview() {
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('orderId');
    const userId = searchParams.get('userId');
    const productId = searchParams.get('productId');
    const productName = searchParams.get('productName') || 'Product';

    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState('');
    const [images, setImages] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');

    // Validate required params
    useEffect(() => {
        if (!orderId || !userId || !productId) {
            setError('Invalid review link. Please contact support.');
        }
    }, [orderId, userId, productId]);

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        if (images.length + files.length > 5) {
            toast.error('Maximum 5 images allowed');
            return;
        }
        
        const newImages = files.map(file => ({
            file,
            preview: URL.createObjectURL(file)
        }));
        setImages(prev => [...prev, ...newImages]);
    };

    const removeImage = (index) => {
        setImages(prev => {
            const newImages = [...prev];
            URL.revokeObjectURL(newImages[index].preview);
            newImages.splice(index, 1);
            return newImages;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (rating === 0) {
            toast.error('Please select a rating');
            return;
        }

        setIsSubmitting(true);

        try {
            const formdata = new FormData();
            formdata.append("userId", userId);
            formdata.append("comment", comment);
            formdata.append("rating", rating.toString());
            formdata.append("orderId", orderId);
            
            // Append all images
            images.forEach((img) => {
                formdata.append("images", img.file);
            });

            const response = await fetch(
                `https://api.alburaqscents.com/v1/product/add-reveiw/${productId}`,
                {
                    method: "PUT",
                    body: formdata,
                    redirect: "follow"
                }
            );

            const result = await response.json();

            if (response.ok && result.success) {
                setIsSubmitted(true);
                toast.success('Thank you for your review!');
            } else {
                throw new Error(result.message || 'Failed to submit review');
            }
        } catch (err) {
            console.error('Review submission error:', err);
            toast.error(err.message || 'Failed to submit review. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const styles = {
        pageWrapper: {
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        },
        container: {
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '24px',
            padding: '40px',
            maxWidth: '550px',
            width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        },
        logo: {
            textAlign: 'center',
            marginBottom: '30px',
        },
        logoText: {
            fontSize: '15px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #D4AF37, #C5A028)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '2px',
        },
        title: {
            fontSize: '17px',
            fontWeight: '600',
            color: '#1a1a2e',
            textAlign: 'center',
            marginBottom: '8px',
        },
        subtitle: {
            fontSize: '14px',
            color: '#666',
            textAlign: 'center',
            marginBottom: '30px',
        },
        productName: {
            color: '#D4AF37',
            fontWeight: '600',
        },
        ratingSection: {
            textAlign: 'center',
            marginBottom: '25px',
        },
        ratingLabel: {
            fontSize: '16px',
            fontWeight: '500',
            color: '#333',
            marginBottom: '15px',
            display: 'block',
        },
        starsContainer: {
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
        },
        star: {
            cursor: 'pointer',
            transition: 'transform 0.2s, color 0.2s',
            fontSize: '15px',
        },
        starActive: {
            color: '#FFD700',
            transform: 'scale(1.1)',
        },
        starInactive: {
            color: '#ddd',
        },
        formGroup: {
            marginBottom: '20px',
        },
        label: {
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#333',
            marginBottom: '8px',
        },
        textarea: {
            width: '100%',
            minHeight: '120px',
            padding: '15px',
            border: '2px solid #e0e0e0',
            borderRadius: '12px',
            fontSize: '14px',
            resize: 'vertical',
            transition: 'border-color 0.3s',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
        },
        uploadArea: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px dashed #D4AF37',
            borderRadius: '12px',
            padding: '30px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.3s',
            backgroundColor: 'rgba(212, 175, 55, 0.05)',
            width: '100%',
            boxSizing: 'border-box',
        },
        uploadIcon: {
            fontSize: '40px',
            color: '#D4AF37',
            marginBottom: '10px',
            display: 'block',
        },
        uploadText: {
            fontSize: '14px',
            color: '#666',
            margin: '0',
        },
        uploadSubtext: {
            fontSize: '12px',
            color: '#999',
            marginTop: '5px',
            margin: '5px 0 0 0',
        },
        imagePreviewContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            marginTop: '15px',
        },
        imagePreview: {
            position: 'relative',
            width: '80px',
            height: '80px',
            borderRadius: '8px',
            overflow: 'hidden',
        },
        previewImage: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        },
        removeImageBtn: {
            position: 'absolute',
            top: '-5px',
            right: '-5px',
            background: '#ff4444',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '22px',
            height: '22px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
        },
        submitButton: {
            width: '100%',
            padding: '16px',
            background: 'linear-gradient(135deg, #D4AF37, #C5A028)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
        },
        submitButtonDisabled: {
            opacity: 0.7,
            cursor: 'not-allowed',
        },
        errorContainer: {
            textAlign: 'center',
            padding: '40px',
        },
        errorText: {
            color: '#ff4444',
            fontSize: '16px',
        },
        successContainer: {
            textAlign: 'center',
            padding: '40px 20px',
        },
        successIcon: {
            fontSize: '80px',
            color: '#4CAF50',
            marginBottom: '20px',
        },
        successTitle: {
            fontSize: '17px',
            fontWeight: '600',
            color: '#333',
            marginBottom: '10px',
        },
        successText: {
            fontSize: '16px',
            color: '#666',
        },
    };

    // Error state
    if (error) {
        return (
            <div style={styles.pageWrapper}>
                <div style={styles.container}>
                    <div style={styles.logo}>
                        <span style={styles.logoText}>ALBURAQ SCENTS</span>
                    </div>
                    <div style={styles.errorContainer}>
                        <p style={styles.errorText}>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (isSubmitted) {
        return (
            <div style={styles.pageWrapper}>
                <div style={styles.container}>
                    <div style={styles.logo}>
                        <span style={styles.logoText}>ALBURAQ SCENTS</span>
                    </div>
                    <div style={styles.successContainer}>
                        <FaCheckCircle style={styles.successIcon} />
                        <h2 style={styles.successTitle}>Thank You!</h2>
                        <p style={styles.successText}>
                            Your review has been submitted successfully. We appreciate your feedback!
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.pageWrapper}>
            <Toaster position="top-center" />
            <div style={styles.container}>
                <div style={styles.logo}>
                    <span style={styles.logoText}>ALBURAQ SCENTS</span>
                </div>
                
                <h1 style={styles.title}>Share Your Experience</h1>
                <p style={styles.subtitle}>
                    How was your experience with <span style={styles.productName}>{decodeURIComponent(productName)}</span>?
                </p>

                <form onSubmit={handleSubmit}>
                    {/* Rating Section */}
                    <div style={styles.ratingSection}>
                        <label style={styles.ratingLabel}>Tap to rate</label>
                        <div style={styles.starsContainer}>
                            {[1, 2, 3, 4, 5].map((star) => (
                                <FaStar
                                    key={star}
                                    style={{
                                        ...styles.star,
                                        ...(star <= (hoverRating || rating)
                                            ? styles.starActive
                                            : styles.starInactive),
                                    }}
                                    onClick={() => setRating(star)}
                                    onMouseEnter={() => setHoverRating(star)}
                                    onMouseLeave={() => setHoverRating(0)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Comment Section */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Your Review (Optional)</label>
                        <textarea
                            style={styles.textarea}
                            placeholder="Tell us about your experience with this product..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </div>

                    {/* Image Upload Section */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Add Photos (Optional)</label>
                        <label style={styles.uploadArea}>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageUpload}
                                style={{ display: 'none' }}
                            />
                            <FaCloudUploadAlt style={styles.uploadIcon} />
                            <p style={styles.uploadText}>Click to upload images</p>
                            <p style={styles.uploadSubtext}>Maximum 5 images • JPG, PNG</p>
                        </label>
                        
                        {images.length > 0 && (
                            <div style={styles.imagePreviewContainer}>
                                {images.map((img, index) => (
                                    <div key={index} style={styles.imagePreview}>
                                        <img
                                            src={img.preview}
                                            alt={`Preview ${index + 1}`}
                                            style={styles.previewImage}
                                        />
                                        <button
                                            type="button"
                                            style={styles.removeImageBtn}
                                            onClick={() => removeImage(index)}
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        style={{
                            ...styles.submitButton,
                            ...(isSubmitting && styles.submitButtonDisabled),
                        }}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Review'}
                    </button>
                </form>
            </div>
        </div>
    );
}

